/**
 * EventsManager — the public-schedule editor.
 *
 * Manages the `event` model: each event is either one-time (`date`) or
 * recurring (`type='recurring'`, `recurringDay`, `recurringTime`,
 * `recurringShowWeeks`). Replaces the old data-table + modal with a
 * scannable Radix card list, a Future/Past/All time filter, a venue
 * filter, show-archived, provenance badges, and client-side pagination.
 *
 * Recurring events have no stored date, so we expand them into real
 * upcoming occurrences with `calculateOccurrences` (logic mirrored from
 * the public Schedule.tsx) — the editor shows actual dates, not an
 * abstract rule.
 */

import { useState, useEffect, useMemo, type FormEvent, type ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  Card,
  Badge,
  Select,
  Switch,
  SegmentedControl,
  TextField,
  Dialog,
  Separator,
} from '@radix-ui/themes';
import { fetchEvents, fetchVenues, createEvent, updateEvent, archiveEvent, deleteEvent } from '../../utils/api';
import HelpTip from '../shared/HelpTip';
import type { Event, Venue, EventFormData } from '../../types';
import './EventsManager.css';

interface SelectOption {
  value: string;
  label: string;
}

const DAYS_OF_WEEK: SelectOption[] = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const PAGE_SIZE = 12;

type TimeFilter = 'future' | 'past' | 'all';

interface FormState {
  name: string;
  venueId: string;
  eventType: 'one-time' | 'recurring';
  date: string;
  time: string;
  recurringDay: string;
  recurringShowWeeks: string;
  imageUrl: string;
  sortOrder: string;
  theme: string;
}

const initialFormState: FormState = {
  name: '',
  venueId: '',
  eventType: 'one-time',
  date: '',
  time: '19:30',
  recurringDay: 'monday',
  recurringShowWeeks: '4',
  imageUrl: '',
  sortOrder: '0',
  theme: '',
};

function eventToFormState(event: Event): FormState {
  const isRecurring = event.type === 'recurring';
  let date = '';
  let time = '19:30';

  if (!isRecurring && event.date) {
    const d = new Date(event.date);
    date = d.toISOString().split('T')[0] ?? '';
    time = d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
  } else if (isRecurring && event.recurringTime) {
    time = event.recurringTime;
  }

  return {
    name: event.name,
    venueId: String(event.venue_id),
    eventType: isRecurring ? 'recurring' : 'one-time',
    date,
    time,
    recurringDay: event.recurringDay ?? 'monday',
    recurringShowWeeks: String(event.recurringShowWeeks ?? 4),
    imageUrl: event.imageUrl ?? '',
    sortOrder: String(event.sortOrder ?? 0),
    theme: event.theme ?? '',
  };
}

/**
 * Expand a recurring event into its upcoming occurrence dates.
 * Mirrors the logic on the public Schedule.tsx so the editor shows the
 * same dates a player would see on the homepage.
 */
function calculateOccurrences(event: Event, weeks: number): Date[] {
  const now = new Date();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  let matchingDay = event.recurringDay;
  if (!matchingDay) {
    matchingDay = days.find((d) => event.name.toLowerCase().includes(d)) ?? null;
  }
  if (!matchingDay) return [now];

  const eventDay = days.indexOf(matchingDay.toLowerCase());
  let diff = eventDay - now.getDay();
  if (diff < 0) diff += 7;

  const first = new Date(now);
  first.setDate(now.getDate() + diff);
  if (event.recurringTime) {
    const [h, m] = event.recurringTime.split(':').map(Number);
    first.setHours(h ?? 19, m ?? 30, 0, 0);
  } else {
    first.setHours(19, 30, 0, 0);
  }

  // If today's occurrence already passed, start from next week
  if (first <= now) {
    first.setDate(first.getDate() + 7);
  }

  const results: Date[] = [];
  for (let i = 0; i < weeks; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() + i * 7);
    results.push(d);
  }
  return results;
}

function formatOccurrence(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatOneTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const EventsManager: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [venueFilter, setVenueFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('future');
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editTarget, setEditTarget] = useState<Event | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // Reset to first page whenever the result set changes shape.
  useEffect(() => {
    setPage(1);
  }, [venueFilter, timeFilter, showArchived]);

  const loadData = async (): Promise<void> => {
    setLoading(true);
    try {
      const [eventsRes, venuesRes] = await Promise.all([
        fetchEvents(showArchived),
        fetchVenues(),
      ]);
      setEvents(eventsRes.data.data.events ?? []);
      setVenues(venuesRes.data ?? []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: keyof FormState, value: string): void => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateDialog = (): void => {
    setEditTarget(null);
    setFormData(initialFormState);
    setIsFormOpen(true);
  };

  const openEditDialog = (event: Event): void => {
    setEditTarget(event);
    setFormData(eventToFormState(event));
    setIsFormOpen(true);
  };

  const openDuplicateDialog = (event: Event): void => {
    setEditTarget(null);
    const form = eventToFormState(event);
    // Clear the date so the user picks a new one for the duplicate.
    if (form.eventType === 'one-time') {
      form.date = '';
    }
    setFormData(form);
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const eventData: EventFormData = {
        name: formData.name,
        venue_id: parseInt(formData.venueId, 10),
        type: 'trivia',
        imageUrl: formData.imageUrl || null,
        sortOrder: parseInt(formData.sortOrder, 10) || 0,
        theme: formData.theme.trim() || null,
      };

      if (formData.eventType === 'one-time') {
        const dateTime = new Date(`${formData.date}T${formData.time}`);
        eventData.date = dateTime.toISOString();
      } else {
        eventData.type = 'recurring';
        eventData.recurringDay = formData.recurringDay;
        eventData.recurringTime = formData.time;
        eventData.recurringShowWeeks = parseInt(formData.recurringShowWeeks, 10) || 4;
      }

      if (editTarget) {
        await updateEvent(editTarget.id, eventData);
        toast.success('Event updated successfully');
      } else {
        await createEvent(eventData);
        toast.success('Event created successfully');
      }

      setIsFormOpen(false);
      setEditTarget(null);
      setFormData(initialFormState);
      loadData();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(editTarget ? 'Failed to update event' : 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveToggle = async (event: Event): Promise<void> => {
    try {
      await archiveEvent(event.id, !event.archived);
      toast.success(event.archived ? 'Event restored' : 'Event archived');
      loadData();
    } catch (error) {
      console.error('Error archiving event:', error);
      toast.error('Failed to archive event');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);

    try {
      await deleteEvent(deleteTarget.id);
      toast.success('Event deleted successfully');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    } finally {
      setIsDeleting(false);
    }
  };

  // ---- Filtering -----------------------------------------------------------

  const now = useMemo(() => new Date(), []);

  const filteredEvents = useMemo(() => {
    let filtered = events;

    if (venueFilter !== 'all') {
      const venueId = parseInt(venueFilter, 10);
      filtered = filtered.filter((event) => (event.venue?.id ?? event.venue_id) === venueId);
    }

    if (timeFilter !== 'all') {
      filtered = filtered.filter((event) => {
        const isRecurring = event.type === 'recurring' && !event.date;
        if (isRecurring) {
          // Recurring events are inherently future-facing.
          return timeFilter === 'future';
        }
        if (!event.date) {
          // One-time with no date: treat as future so it stays visible.
          return timeFilter === 'future';
        }
        const isFuture = new Date(event.date) >= now;
        return timeFilter === 'future' ? isFuture : !isFuture;
      });
    }

    return filtered;
  }, [events, venueFilter, timeFilter, now]);

  // ---- Pagination ----------------------------------------------------------

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEvents = filteredEvents.slice(pageStart, pageStart + PAGE_SIZE);

  // ---- Render helpers ------------------------------------------------------

  const venueName = (event: Event): string =>
    event.venue?.name ?? venues.find((v) => v.id === event.venue_id)?.name ?? 'Unknown venue';

  const renderSchedule = (event: Event): React.ReactNode => {
    const isRecurring = event.type === 'recurring' && !event.date;

    if (isRecurring) {
      const weeks = event.recurringShowWeeks ?? 4;
      const occurrences = calculateOccurrences(event, Math.min(weeks, 3));
      const dayLabel =
        DAYS_OF_WEEK.find((d) => d.value === event.recurringDay)?.label ?? event.recurringDay ?? '';
      return (
        <Flex direction="column" gap="1">
          <Text size="2" color="gray">
            Every {dayLabel} at {event.recurringTime ?? '19:30'}
          </Text>
          <Flex gap="1" wrap="wrap">
            {occurrences.map((d, i) => (
              <Badge key={i} color="gray" variant="soft" radius="full">
                {formatOccurrence(d)}
              </Badge>
            ))}
          </Flex>
        </Flex>
      );
    }

    if (event.date) {
      return (
        <Text size="2" color="gray">
          {formatOneTime(event.date)}
        </Text>
      );
    }

    return (
      <Text size="2" color="gray">
        No date set
      </Text>
    );
  };

  const venueOptions: SelectOption[] = useMemo(
    () => [{ value: 'all', label: 'All Venues' }, ...venues.map((v) => ({ value: String(v.id), label: v.name }))],
    [venues],
  );

  // ---- JSX -----------------------------------------------------------------

  return (
    <Box className="events-manager">
      <Flex className="events-header" justify="between" align="start" gap="3" wrap="wrap">
        <Box>
          <Heading size="6">
            Public Schedule
            <HelpTip side="bottom">
              This edits what visitors see on your website — not the game nights you
              host. Those live under Run → Game Nights.
            </HelpTip>
          </Heading>
          <Text size="2" color="gray">
            What visitors see on your website — not the game nights you host.
          </Text>
        </Box>
        <Button onClick={openCreateDialog}>
          <PlusIcon /> Add Event
        </Button>
      </Flex>

      <Card className="events-toolbar">
        <Flex align="center" gap="4" wrap="wrap">
          <SegmentedControl.Root
            value={timeFilter}
            onValueChange={(v) => setTimeFilter(v as TimeFilter)}
            size="2"
          >
            <SegmentedControl.Item value="future">Future</SegmentedControl.Item>
            <SegmentedControl.Item value="past">Past</SegmentedControl.Item>
            <SegmentedControl.Item value="all">All</SegmentedControl.Item>
          </SegmentedControl.Root>

          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Venue
            </Text>
            <Select.Root value={venueFilter} onValueChange={setVenueFilter}>
              <Select.Trigger placeholder="All Venues" />
              <Select.Content>
                {venueOptions.map((opt) => (
                  <Select.Item key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>

          <Text as="label" size="2" color="gray">
            <Flex align="center" gap="2">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} size="1" />
              Show archived
            </Flex>
          </Text>

          <Box flexGrow="1" />

          <Text size="2" color="gray">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </Text>
        </Flex>
      </Card>

      {loading ? (
        <Card>
          <Text color="gray">Loading events…</Text>
        </Card>
      ) : pageEvents.length === 0 ? (
        <Card>
          <Flex direction="column" align="center" gap="2" py="6">
            {events.length === 0 ? (
              <>
                <Text color="gray">Nothing on your public schedule yet.</Text>
                <Text size="2" color="gray" align="center" style={{ maxWidth: 460 }}>
                  Add the nights you run each week and they&apos;ll appear on your
                  website&apos;s homepage so players know when and where to show up.
                </Text>
              </>
            ) : (
              <Text color="gray">No events match this filter.</Text>
            )}
            <Button variant="soft" onClick={openCreateDialog}>
              <PlusIcon /> Add your first event
            </Button>
          </Flex>
        </Card>
      ) : (
        <Flex direction="column" gap="3">
          {pageEvents.map((event) => {
            const isRecurring = event.type === 'recurring' && !event.date;
            return (
              <Card key={event.id} className={`event-card${event.archived ? ' event-card--archived' : ''}`}>
                <Flex justify="between" gap="3" align="start" wrap="wrap">
                  <Flex gap="3" align="start" className="event-card__main">
                    {event.imageUrl && (
                      <img
                        src={event.imageUrl}
                        alt=""
                        className="event-card__thumb"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <Flex direction="column" gap="2">
                      <Flex align="center" gap="2" wrap="wrap">
                        <Text size="4" weight="bold">
                          {event.name}
                        </Text>
                        <Badge color={isRecurring ? 'iris' : 'green'} variant="soft" radius="full">
                          {isRecurring ? 'Recurring' : 'One-time'}
                        </Badge>
                        {event.theme && (
                          <Badge color="amber" variant="soft" radius="full">
                            {event.theme}
                          </Badge>
                        )}
                        {event.archived && (
                          <Badge color="gray" variant="outline" radius="full">
                            Archived
                          </Badge>
                        )}
                      </Flex>
                      <Text size="2" color="gray">
                        {venueName(event)}
                      </Text>
                      {renderSchedule(event)}
                    </Flex>
                  </Flex>

                  <Flex gap="2" wrap="wrap" justify="end" className="event-card__actions">
                    <Button size="1" variant="soft" onClick={() => openEditDialog(event)}>
                      Edit
                    </Button>
                    <Button
                      size="1"
                      variant="soft"
                      color="gray"
                      onClick={() => openDuplicateDialog(event)}
                      title="Duplicate this event"
                    >
                      Duplicate
                    </Button>
                    <Button size="1" variant="soft" color="gray" onClick={() => handleArchiveToggle(event)}>
                      {event.archived ? 'Restore' : 'Archive'}
                    </Button>
                    <Button size="1" variant="soft" color="red" onClick={() => setDeleteTarget(event)}>
                      Delete
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            );
          })}
        </Flex>
      )}

      {!loading && filteredEvents.length > 0 && (
        <Flex align="center" justify="center" gap="3" mt="1">
          <Button
            variant="soft"
            color="gray"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </Button>
          <Text size="2" color="gray">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            variant="soft"
            color="gray"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next →
          </Button>
        </Flex>
      )}

      {/* Create / Edit Event Dialog */}
      <Dialog.Root
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setEditTarget(null);
        }}
      >
        <Dialog.Content maxWidth="520px">
          <Dialog.Title>{editTarget ? 'Edit Event' : 'Create New Event'}</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="4">
            {editTarget ? 'Update event details.' : 'Add a new trivia event to your schedule.'}
          </Dialog.Description>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Event Name
                </Text>
                <TextField.Root
                  name="name"
                  placeholder="e.g., Tuesday Trivia Night"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Venue
                </Text>
                <Select.Root
                  value={formData.venueId}
                  onValueChange={(val) => handleSelectChange('venueId', val)}
                  required
                >
                  <Select.Trigger placeholder="Select a venue" className="dialog-select-trigger" />
                  <Select.Content>
                    {venues.map((v) => (
                      <Select.Item key={v.id} value={String(v.id)}>
                        {v.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </label>

              <Box>
                <Text as="div" size="2" weight="medium" mb="1">
                  Event Type
                </Text>
                <SegmentedControl.Root
                  value={formData.eventType}
                  onValueChange={(val) => handleSelectChange('eventType', val as 'one-time' | 'recurring')}
                >
                  <SegmentedControl.Item value="one-time">One-time</SegmentedControl.Item>
                  <SegmentedControl.Item value="recurring">Recurring</SegmentedControl.Item>
                </SegmentedControl.Root>
              </Box>

              {formData.eventType === 'one-time' ? (
                <Flex gap="3" className="dialog-form-row">
                  <label style={{ flex: 1 }}>
                    <Text as="div" size="2" weight="medium" mb="1">
                      Date
                    </Text>
                    <TextField.Root type="date" name="date" value={formData.date} onChange={handleInputChange} required />
                  </label>
                  <label style={{ flex: 1 }}>
                    <Text as="div" size="2" weight="medium" mb="1">
                      Time
                    </Text>
                    <TextField.Root type="time" name="time" value={formData.time} onChange={handleInputChange} required />
                  </label>
                </Flex>
              ) : (
                <>
                  <Flex gap="3" className="dialog-form-row">
                    <Box style={{ flex: 1 }}>
                      <Text as="div" size="2" weight="medium" mb="1">
                        Recurring Day
                      </Text>
                      <Select.Root
                        value={formData.recurringDay}
                        onValueChange={(val) => handleSelectChange('recurringDay', val)}
                      >
                        <Select.Trigger className="dialog-select-trigger" />
                        <Select.Content>
                          {DAYS_OF_WEEK.map((d) => (
                            <Select.Item key={d.value} value={d.value}>
                              {d.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Box>
                    <label style={{ flex: 1 }}>
                      <Text as="div" size="2" weight="medium" mb="1">
                        Time
                      </Text>
                      <TextField.Root type="time" name="time" value={formData.time} onChange={handleInputChange} required />
                    </label>
                  </Flex>
                  <label>
                    <Text as="div" size="2" weight="medium" mb="1">
                      Weeks to show on homepage
                    </Text>
                    <TextField.Root
                      type="number"
                      name="recurringShowWeeks"
                      value={formData.recurringShowWeeks}
                      onChange={handleInputChange}
                      min={1}
                      max={52}
                    />
                  </label>
                </>
              )}

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Theme (optional)
                </Text>
                <TextField.Root
                  name="theme"
                  placeholder="e.g. 80s Music Night, Halloween"
                  value={formData.theme}
                  onChange={handleInputChange}
                  maxLength={80}
                />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Sort Order (lower = earlier on homepage)
                </Text>
                <TextField.Root type="number" name="sortOrder" value={formData.sortOrder} onChange={handleInputChange} min={0} />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Event Image URL (optional)
                </Text>
                <TextField.Root
                  type="url"
                  name="imageUrl"
                  placeholder="https://triviakeg.nyc3.digitaloceanspaces.com/..."
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                />
                {formData.imageUrl && (
                  <img
                    src={formData.imageUrl}
                    alt="Event preview"
                    className="dialog-image-preview"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </label>
            </Flex>

            <Separator size="4" my="4" />

            <Flex gap="3" justify="end">
              <Dialog.Close>
                <Button type="button" variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? editTarget
                    ? 'Saving…'
                    : 'Creating…'
                  : editTarget
                    ? 'Save Changes'
                    : 'Create Event'}
              </Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>Delete Event</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? This action cannot be undone.
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button color="red" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
};

const PlusIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default EventsManager;
